import { Button } from '../ui';
import { signal } from '@preact/signals';

interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => any;
  sortable?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  pageSize?: number;
  emptyMessage?: string;
}

export function DataTable<T extends Record<string, any>>({ columns, data, loading, pageSize = 10, emptyMessage = 'Nenhum dado' }: DataTableProps<T>) {
  const page = signal(0);
  const sortKey = signal('');
  const sortAsc = signal(true);

  if (loading) {
    return (
      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden animate-pulse">
        <div class="h-10 bg-gray-100" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} class="h-12 border-t border-gray-100 px-4 flex items-center">
            <div class="h-3 bg-gray-200 rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  let sorted = [...data];
  if (sortKey.value) {
    sorted.sort((a, b) => {
      const va = a[sortKey.value]; const vb = b[sortKey.value];
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortAsc.value ? cmp : -cmp;
    });
  }

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice(page.value * pageSize, (page.value + 1) * pageSize);

  function toggleSort(key: string) {
    if (sortKey.value === key) sortAsc.value = !sortAsc.value;
    else { sortKey.value = key; sortAsc.value = true; }
  }

  if (!data.length) {
    return (
      <div class="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 text-left">
              {columns.map(col => (
                <th
                  key={col.key}
                  class={`px-4 py-3 font-medium text-gray-600 ${col.sortable !== false ? 'cursor-pointer hover:text-gray-900 select-none' : ''}`}
                  onClick={() => col.sortable !== false && toggleSort(col.key)}
                >
                  {col.label}
                  {sortKey.value === col.key && (sortAsc.value ? ' ↑' : ' ↓')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, i) => (
              <tr key={i} class="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                {columns.map(col => (
                  <td key={col.key} class="px-4 py-3 text-gray-700">
                    {col.render ? col.render(row) : String(row[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div class="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
          <span>{sorted.length} itens</span>
          <div class="flex gap-2">
            <Button variant="ghost"
              class="px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
              disabled={page.value === 0}
              onClick={() => page.value--}
            >←</Button>
            <span class="px-2 py-1">{page.value + 1}/{totalPages}</span>
            <Button variant="ghost"
              class="px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
              disabled={page.value >= totalPages - 1}
              onClick={() => page.value++}
            >→</Button>
          </div>
        </div>
      )}
    </div>
  );
}
